import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, X, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

export function LoginModal({ open, onClose }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
      onClose();
      setEmail("");
      setPassword("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div
            data-testid="admin-login-modal"
            className="fixed inset-x-0 top-1/2 z-[61] mx-auto max-w-sm -translate-y-1/2 rounded-2xl border border-white/10 p-6"
            style={{ background: "rgba(10,14,23,0.98)" }}
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                <Lock size={17} className="text-amber-300" /> Admin login
              </h2>
              <button data-testid="admin-login-close-btn" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-slate-300"><X size={15} /></button>
            </div>
            <p className="mt-1 text-xs text-slate-400">Sign in to manage destination content.</p>
            <form onSubmit={submit} className="mt-4 space-y-3">
              <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-amber-300/40" />
              <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-amber-300/40" />
              {error && <p data-testid="admin-login-error" className="text-xs text-red-400">{error}</p>}
              <button data-testid="admin-login-submit-btn" type="submit" disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-300 py-2.5 text-sm font-semibold text-[#07090E] transition-transform active:scale-[0.98] disabled:opacity-60">
                {loading ? <Loader2 size={15} className="animate-spin" /> : null}
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
